import { z } from '@customerio/hydra-validation';

/**
 * What a `metrics` custom report reads: one subject's delivery counters over
 * a window, cut into buckets. The metrics evaluator answers with counts only,
 * so `value: 'rate'` is the chart's business: it asks for the counters each
 * rate divides by as well and computes the rates itself.
 *
 * Like the rest of the custom reports wire shape, this is declared here until
 * the services spec carries it.
 */

const metricsReportSubjects = ['campaign', 'broadcast', 'transactional', 'workspace'] as const;

/** A rolling window, spelled as the analytics query spec spells one. */
const RelativeRangeSchema = z.object({
  relative: z.object({ n: z.number().int().positive(), unit: z.enum(['day', 'week', 'month']) }),
});

/** An exact window, as ISO dates, both included. */
const AbsoluteRangeSchema = z.object({ from: z.iso.date(), to: z.iso.date() });

export const MetricsReportDefinitionSchema = z.object({
  subject: z.enum(metricsReportSubjects),
  /** The campaign, broadcast or transactional message; left out for the workspace. */
  id: z.number().int().positive().optional(),
  /** Delivery channels to read; every channel when left out. */
  channels: z.array(z.string()).optional(),
  /** Delivery metric names, e.g. `opened`. */
  metrics: z.array(z.string()).min(1),
  range: z.union([RelativeRangeSchema, AbsoluteRangeSchema]),
  /** Calendar buckets only: the chart reads a bucket as the day it starts on. */
  resolution: z.enum(['days', 'weeks', 'months']),
  /** Whether the chart draws the counts or their rates. Counts when left out. */
  value: z.enum(['count', 'rate']).optional(),
});

export type MetricsReportDefinition = z.output<typeof MetricsReportDefinitionSchema>;

/** A stored definition as a metrics definition, or null when it is not one. */
export function parseMetricsReportDefinition(definition: unknown): MetricsReportDefinition | null {
  const parsed = MetricsReportDefinitionSchema.safeParse(definition);
  return parsed.success ? parsed.data : null;
}
